import type { PlayerState } from "../../model/PlayerState.js";
import type { Roll } from "../../model/dice/Roll.js";
import type { Board } from "../../model/Board.js";
import type { PlayerType } from "../../model/PlayerType.js";
import { PlayableGameState } from "./PlayableGameState.js";
import { Move } from "../../model/Move.js";
import type { DescribePlayerNames } from "./GameState.js";
import { getDescribePlayerName } from "./GameState.js";
import { isSameRuntimeType } from "../../../util/tsutils.js";


/**
 * A game state where the game is waiting for a player to make a move.
 */
export class WaitingForMoveGameState extends PlayableGameState {
    private readonly roll: Roll;
    private readonly availableMoves: Move[];

    constructor(
        board: Board,
        lightPlayer: PlayerState,
        darkPlayer: PlayerState,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
        availableMoves: Move[],
    ) {
        super(board, lightPlayer, darkPlayer, timeSinceGameStartMs, turn);
        if (roll.value() <= 0) {
            throw new Error(
                "Roll must have a value of at least 1, not " + roll.value() + ".",
            );
        }

        this.roll = roll;
        this.availableMoves = availableMoves;
    }

    getRoll(): Roll {
        return this.roll;
    }

    getAvailableMoves(): Move[] {
        return this.availableMoves;
    }

    override describe(playerNames?: DescribePlayerNames): string {
        const playerName = getDescribePlayerName(this.getTurn(), playerNames);
        const roll = this.roll.value();
        return `Waiting for ${playerName} to make a move with their roll of ${roll}`;
    }

    override equals(other: unknown): boolean {
        if (other === this)
            return true;
        if (!isSameRuntimeType(this, other))
            return false;

        return PlayableGameState._baselineEquals(this, other)
            && this.roll.equals(other.roll)
            && Move.setEquals(this.availableMoves, other.availableMoves);
    }
}
